import { useState } from "react";
import { Facebook, Link as LinkIcon, Check, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { buildShareUrl, withUtm } from "../lib/social";

/**
 * Branded X (Twitter) monogram , Lucide doesn't ship the current one.
 */
const XIcon = ({ size = 16, className = "" }) => (
  <svg
    viewBox="0 0 24 24"
    width={size}
    height={size}
    aria-hidden="true"
    fill="currentColor"
    className={className}
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817-5.97 6.817H1.674l7.73-8.836L1.254 2.25h6.828l4.713 6.231 5.45-6.231Zm-1.16 17.52h1.833L7.084 4.126H5.117l11.967 15.643Z" />
  </svg>
);

/**
 * Per-study share row. Rare-disease + chronic-condition communities
 * heavily share studies in Facebook groups and WhatsApp chats, so a
 * one-tap share path materially lowers the friction for a study
 * candidate to reach us via a friend.
 *
 * The shared URL is always tagged with UTMs so we can see in PostHog
 * which platform actually delivers the most candidates.
 *
 * Instagram and TikTok aren't here because neither supports URL-based
 * share intents from the web , the only "share" they offer is in-app.
 */
export default function ShareButtons({
  url,
  text,
  source = "study-detail",
  className = "",
  variant = "light", // "light" | "dark"
}) {
  const [copied, setCopied] = useState(false);

  const isDark = variant === "dark";
  const tileBase = isDark
    ? "bg-white/[0.06] border-white/10 text-white hover:bg-[var(--lime)] hover:text-[var(--dark-bg)] hover:border-[var(--lime)]"
    : "bg-white border-[var(--light-border)] text-[var(--charcoal)] hover:bg-[var(--lime)] hover:text-[var(--dark-bg)] hover:border-[var(--lime)]";

  const buttons = [
    {
      id: "whatsapp",
      label: "WhatsApp",
      Icon: MessageCircle,
      href: buildShareUrl("whatsapp", {
        url: withUtm(url, { source: "share-whatsapp", medium: "social", campaign: source }),
        text,
      }),
    },
    {
      id: "x",
      label: "X",
      Icon: XIcon,
      href: buildShareUrl("x", {
        url: withUtm(url, { source: "share-x", medium: "social", campaign: source }),
        text,
      }),
    },
    {
      id: "facebook",
      label: "Facebook",
      Icon: Facebook,
      href: buildShareUrl("facebook", {
        url: withUtm(url, { source: "share-facebook", medium: "social", campaign: source }),
        text,
      }),
    },
  ];

  const onCopy = async () => {
    try {
      const tagged = withUtm(url, {
        source: "share-copy",
        medium: "social",
        campaign: source,
      });
      await navigator.clipboard.writeText(tagged);
      setCopied(true);
      toast.success("Link copied , share away.");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy. Long-press the URL to copy it manually.");
    }
  };

  return (
    <div
      className={`flex flex-wrap items-center gap-2 ${className}`}
      data-testid="share-buttons"
      role="group"
      aria-label="Share this study"
    >
      <span
        className={`text-[12px] uppercase tracking-[0.16em] font-semibold mr-1 ${isDark ? "text-white/70" : "text-[var(--text-muted)]"}`}
      >
        Share
      </span>
      {buttons.map(({ id, label, Icon, href }) => (
        <a
          key={id}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Share on ${label} (opens in new tab)`}
          title={`Share on ${label}`}
          className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] font-semibold transition-colors ${tileBase}`}
          data-testid={`share-${id}`}
        >
          <Icon size={14} />
          <span className="hidden sm:inline">{label}</span>
        </a>
      ))}
      <button
        type="button"
        onClick={onCopy}
        aria-label="Copy share link"
        title="Copy link"
        className={`inline-flex items-center gap-1.5 px-3 py-2 rounded-full border text-[13px] font-semibold transition-colors ${tileBase}`}
        data-testid="share-copy"
      >
        {copied ? <Check size={14} strokeWidth={3} /> : <LinkIcon size={14} />}
        <span className="hidden sm:inline">{copied ? "Copied" : "Copy link"}</span>
      </button>
    </div>
  );
}
